import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Badge,
  Box,
  Button,
  HoverCard,
  HStack,
  IconButton,
  Input,
  MenuContent,
  MenuItem,
  MenuPositioner,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
  Skeleton,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react'
import { LuCheck, LuInfo, LuListFilter, LuListStart, LuLoader } from 'react-icons/lu'
import type { CapacityStatus, SibCapacityRow, SortDir, SortField } from '../capacity.types'
import {
  capacityUsagePct,
  formatQuantity,
  lastName,
  parseNumber,
  statusColor,
  statusLabel,
  CAPACITY_UNITS,
} from '../capacity.utils'
import { ZeitzielBadge } from './ZeitzielCard'

const stickyColStyles = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 2,
  bg: 'bg',
  borderRightWidth: '1px',
  borderRightColor: 'border',
}
const stickyHeaderStyles = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 4,
  bg: 'bg',
  borderRightWidth: '1px',
  borderRightColor: 'border',
}
const cellProps = { px: 4, py: 4, verticalAlign: 'middle' as const }

function StatusLegend({ status, description }: { status: CapacityStatus; description: string }) {
  return (
    <HStack gap={2} align="start">
      <Box
        w="10px"
        h="10px"
        borderRadius="full"
        bg={statusColor(status)}
        mt="4px"
        flexShrink={0}
      />
      <Box>
        <Text fontSize="sm" color="fg">
          {statusLabel(status)}
        </Text>
        <Text fontSize="xs" color="fg.subtle">
          {description}
        </Text>
      </Box>
    </HStack>
  )
}

function InfoHoverCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <HoverCard.Root openDelay={120} closeDelay={80}>
      <HoverCard.Trigger asChild>
        <IconButton aria-label={`${label} Info`} size="xs" variant="ghost">
          <LuInfo />
        </IconButton>
      </HoverCard.Trigger>
      <HoverCard.Positioner zIndex={9999}>
        <HoverCard.Content
          bg="bg"
          borderWidth="1px"
          borderColor="border"
          p={4}
          shadow="md"
          maxW="360px"
        >
          <HoverCard.Arrow />
          <Stack gap={2} whiteSpace="normal">
            {children}
          </Stack>
        </HoverCard.Content>
      </HoverCard.Positioner>
    </HoverCard.Root>
  )
}

function ColHeader({ label, tooltip }: { label: string; tooltip?: React.ReactNode }) {
  if (!tooltip) return <Text>{label}</Text>
  return (
    <HStack gap={1.5}>
      <Text>{label}</Text>
      <InfoHoverCard label={label}>{tooltip}</InfoHoverCard>
    </HStack>
  )
}

function sortRows(rows: SibCapacityRow[], field: SortField, dir: SortDir): SibCapacityRow[] {
  return [...rows].sort((a, b) => {
    let cmp = 0
    switch (field) {
      case 'name':
        cmp = lastName(a.sib).localeCompare(lastName(b.sib), 'de')
        break
      case 'usage':
        cmp = parseNumber(a.usage) - parseNumber(b.usage)
        break
      case 'target':
        cmp = parseNumber(a.target) - parseNumber(b.target)
        break
      case 'revenue':
        cmp = parseNumber(a.revenue) - parseNumber(b.revenue)
        break
      case 'place':
        cmp = a.place.localeCompare(b.place, 'de')
        break
      case 'region':
        cmp = a.region.localeCompare(b.region, 'de')
        break
    }
    return dir === 'asc' ? cmp : -cmp
  })
}

const SORT_OPTIONS: { label: string; value: SortField }[] = [
  { label: 'Alphabetisch', value: 'name' },
  { label: 'Auslastung', value: 'usage' },
  { label: 'Zeitziel', value: 'target' },
  { label: 'Planumsatz', value: 'revenue' },
  { label: 'PLZ', value: 'place' },
  { label: 'Vxx', value: 'region' },
]

const STATUS_FILTERS: { label: string; value: CapacityStatus | 'all' }[] = [
  { label: 'Alle', value: 'all' },
  { label: 'Im Plan', value: 'green' },
  { label: 'Prüfen', value: 'yellow' },
  { label: 'Auffällig', value: 'red' },
]

export function SibCapacityTable({
  rows: sourceRows,
  onOpenDetail,
  isLoading = false,
  isLoadingMore = false,
  totalCount,
  tabs,
}: {
  rows: SibCapacityRow[]
  onOpenDetail: (userId: string) => void
  isLoading?: boolean
  isLoadingMore?: boolean
  totalCount?: number
  tabs?: React.ReactNode
}) {
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [statusFilter, setStatusFilter] = useState<CapacityStatus | 'all'>('all')

  const scrollRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const check = () => setHasOverflow(el.scrollWidth > el.clientWidth)
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const rows = useMemo(() => {
    let result = sourceRows
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        r =>
          (r.sib ?? '').toLowerCase().includes(q) ||
          (r.contacts ?? []).some(c => c.toLowerCase().includes(q)) ||
          (r.place ?? '').toLowerCase().includes(q) ||
          (r.region ?? '').toLowerCase().includes(q) ||
          String(r.radius ?? '')
            .toLowerCase()
            .includes(q)
      )
    }
    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter)
    }
    return sortRows(result, sortField, sortDir)
  }, [sourceRows, search, sortField, sortDir, statusFilter])

  if (isLoading) {
    return (
      <Stack gap={4} data-testid="sib-capacity-table">
        <HStack w="full" gap={3} align="center" flexWrap="wrap">
          <Input flex="1" minW="240px" placeholder="SIB suchen, Name oder Ort eingeben" disabled />
          {tabs}
        </HStack>
        <Box borderWidth="1px" borderColor="border" borderRadius="xl" overflow="hidden" bg="bg">
          <Stack gap={2} p={4}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} h="48px" borderRadius="md" />
            ))}
          </Stack>
        </Box>
      </Stack>
    )
  }

  return (
    <Stack gap={4} data-testid="sib-capacity-table">
      {/* Toolbar */}
      <HStack w="full" gap={3} align="center" flexWrap="wrap">
        <Input
          flex="1"
          minW="240px"
          placeholder="SIB suchen, Name oder Ort eingeben"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {tabs}

        {/* Sort */}
        <MenuRoot positioning={{ placement: 'bottom-end', gutter: 8 }}>
          <MenuTrigger asChild>
            <Button variant="subtle" size="sm">
              <HStack gap={2}>
                <LuListStart />
                <Text>Sortierung</Text>
              </HStack>
            </Button>
          </MenuTrigger>
          <MenuPositioner>
            <MenuContent minW="220px" bg="bg" borderColor="border">
              {SORT_OPTIONS.map(opt => (
                <MenuItem
                  key={opt.value}
                  value={`sort:${opt.value}`}
                  onClick={() => setSortField(opt.value)}
                >
                  <HStack justify="space-between" w="full">
                    <Text>{opt.label}</Text>
                    {sortField === opt.value ? <LuCheck size={14} /> : null}
                  </HStack>
                </MenuItem>
              ))}
              <MenuSeparator />
              <MenuItem value="dir:asc" onClick={() => setSortDir('asc')}>
                <HStack justify="space-between" w="full">
                  <Text>Aufsteigend</Text>
                  {sortDir === 'asc' ? <LuCheck size={14} /> : null}
                </HStack>
              </MenuItem>
              <MenuItem value="dir:desc" onClick={() => setSortDir('desc')}>
                <HStack justify="space-between" w="full">
                  <Text>Absteigend</Text>
                  {sortDir === 'desc' ? <LuCheck size={14} /> : null}
                </HStack>
              </MenuItem>
            </MenuContent>
          </MenuPositioner>
        </MenuRoot>

        {/* Filter */}
        <MenuRoot positioning={{ placement: 'bottom-end', gutter: 8 }}>
          <MenuTrigger asChild>
            <Button
              variant={statusFilter !== 'all' ? 'solid' : 'subtle'}
              colorPalette={statusFilter !== 'all' ? 'teal' : 'gray'}
              size="sm"
            >
              <HStack gap={2}>
                <LuListFilter />
                <Text>Filter</Text>
              </HStack>
            </Button>
          </MenuTrigger>
          <MenuPositioner>
            <MenuContent minW="200px" bg="bg" borderColor="border">
              {STATUS_FILTERS.map(f => (
                <MenuItem
                  key={f.value}
                  value={`filter:${f.value}`}
                  onClick={() => setStatusFilter(f.value)}
                >
                  <HStack justify="space-between" w="full">
                    <Text>{f.label}</Text>
                    {statusFilter === f.value ? <LuCheck size={14} /> : null}
                  </HStack>
                </MenuItem>
              ))}
            </MenuContent>
          </MenuPositioner>
        </MenuRoot>
      </HStack>

      {/* Table */}
      <Box borderWidth="1px" borderColor="border" borderRadius="xl" overflow="hidden" bg="bg">
        {isLoadingMore && search.trim() && (
          <HStack
            px={4}
            py={2}
            gap={2}
            bg="blue.subtle"
            borderBottomWidth="1px"
            borderColor="blue.muted"
          >
            <Box color="blue.fg" flexShrink={0}>
              <LuLoader size={13} />
            </Box>
            <Text fontSize="xs" color="blue.fg">
              Noch nicht alle Daten geladen – Suche läuft auf {sourceRows.length}
              {totalCount ? ` von ${totalCount}` : ''} SIBs. Weitere Ergebnisse erscheinen
              automatisch.
            </Text>
          </HStack>
        )}
        <Box position="relative">
          <Box ref={scrollRef} overflowX="auto">
            <Table.Root native minW="1200px" bg="bg">
              <Table.Header bg="bg">
                <Table.Row>
                  <Table.ColumnHeader
                    w="260px"
                    px={4}
                    py={5}
                    fontSize="md"
                    fontWeight="700"
                    color="fg.default"
                    borderBottomWidth="1px"
                    borderColor="border"
                    {...stickyHeaderStyles}
                  >
                    <ColHeader
                      label="SIB"
                      tooltip={
                        <Stack gap={1.5}>
                          <Text fontSize="sm" fontWeight="semibold">
                            SIB
                          </Text>
                          <StatusLegend
                            status="green"
                            description="unauffällig im Verhältnis zur Kapazität"
                          />
                          <StatusLegend
                            status="yellow"
                            description="prüfbedürftig am Grenzbereich"
                          />
                          <StatusLegend
                            status="red"
                            description="überbucht gegenüber der Kapazität"
                          />
                          <Text fontSize="sm" color="fg.subtle">
                            Der Planumsatz wird aus der vereinbarten Kapazität und den
                            individuellen Preisen des SIB berechnet.
                          </Text>
                        </Stack>
                      }
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    w="160px"
                    px={4}
                    py={5}
                    fontSize="md"
                    fontWeight="700"
                    color="fg.default"
                    borderBottomWidth="1px"
                    borderLeftWidth="1px"
                    borderColor="border"
                  >
                    PLZ / Ort
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    w="90px"
                    px={4}
                    py={5}
                    fontSize="md"
                    fontWeight="700"
                    color="fg.default"
                    borderBottomWidth="1px"
                    borderLeftWidth="1px"
                    borderColor="border"
                  >
                    Vxx
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    w="160px"
                    px={4}
                    py={5}
                    fontSize="md"
                    fontWeight="700"
                    color="fg.default"
                    borderBottomWidth="1px"
                    borderLeftWidth="1px"
                    borderColor="border"
                  >
                    <ColHeader
                      label="Wirkungsbereich"
                      tooltip={
                        <Text fontSize="sm" color="fg">
                          Der Wirkungsbereich des SIBs, zum Beispiel als Kilometer-Umkreis,
                          Bundesland oder länderübergreifender Bereich.
                        </Text>
                      }
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    w="290px"
                    px={4}
                    py={5}
                    fontSize="md"
                    fontWeight="700"
                    color="fg.default"
                    borderBottomWidth="1px"
                    borderLeftWidth="1px"
                    borderColor="border"
                  >
                    <ColHeader
                      label="Kapazität"
                      tooltip={
                        <Text fontSize="sm" color="fg">
                          Die Kapazität zeigt den vereinbarten Jahresrahmen je Leistung. Erfasst
                          werden TOJ, Beratungsstunden, Schulungstage und Pilotstunden.
                        </Text>
                      }
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    w="340px"
                    px={4}
                    py={5}
                    fontSize="md"
                    fontWeight="700"
                    color="fg.default"
                    borderBottomWidth="1px"
                    borderLeftWidth="1px"
                    borderColor="border"
                  >
                    <ColHeader
                      label="Bestellt"
                      tooltip={
                        <Stack gap={1}>
                          <Text fontSize="sm" color="fg">
                            Die bestätigte Menge im Verhältnis zur vereinbarten Kapazität.
                          </Text>
                          <Text fontSize="sm" color="fg">
                            Werte über 100 % bedeuten eine Überschreitung der Kapazität.
                          </Text>
                        </Stack>
                      }
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    w="120px"
                    px={4}
                    py={5}
                    fontSize="md"
                    fontWeight="700"
                    color="fg.default"
                    borderBottomWidth="1px"
                    borderLeftWidth="1px"
                    borderColor="border"
                  >
                    <ColHeader
                      label="Zeitziel"
                      tooltip={
                        <Text fontSize="sm" color="fg">
                          Das Zeitziel zeigt, ob die Entwicklung im laufenden Jahr dem erwarteten
                          zeitlichen Verlauf folgt.
                        </Text>
                      }
                    />
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={7} textAlign="center" py={12} color="fg.subtle">
                      Keine Einträge gefunden.
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  rows.map(row => (
                    <Table.Row
                      data-testid={`sib-row-${row.userId}`}
                      key={row.userId ?? row.sib}
                      cursor="pointer"
                      _hover={{ bg: 'bg.subtle' }}
                      onClick={() => row.userId && onOpenDetail(row.userId)}
                      css={{
                        '&:hover > td[data-sticky="true"]': {
                          backgroundColor: 'var(--chakra-colors-bg-subtle) !important',
                        },
                      }}
                    >
                      <Table.Cell
                        {...cellProps}
                        w="260px"
                        borderTopWidth="1px"
                        borderColor="border"
                        {...stickyColStyles}
                        data-sticky="true"
                        bg="bg"
                      >
                        <HStack gap={2} align="start">
                          <Box
                            w="10px"
                            h="10px"
                            borderRadius="full"
                            bg={statusColor(row.status)}
                            mt="5px"
                            flexShrink={0}
                          />
                          <Stack gap={0} minW={0}>
                            <Text
                              fontWeight="medium"
                              color="fg"
                              whiteSpace="nowrap"
                              overflow="hidden"
                              textOverflow="ellipsis"
                            >
                              {row.sib}
                            </Text>
                            <Text fontSize="xs" color="fg.subtle" whiteSpace="nowrap">
                              {row.revenue} Planumsatz
                            </Text>
                          </Stack>
                        </HStack>
                      </Table.Cell>
                      <Table.Cell
                        {...cellProps}
                        borderTopWidth="1px"
                        borderLeftWidth="1px"
                        borderColor="border"
                        color="fg.muted"
                        whiteSpace="nowrap"
                      >
                        {row.place}
                      </Table.Cell>
                      <Table.Cell
                        {...cellProps}
                        borderTopWidth="1px"
                        borderLeftWidth="1px"
                        borderColor="border"
                        color="fg.muted"
                        whiteSpace="nowrap"
                      >
                        {row.region}
                      </Table.Cell>
                      <Table.Cell
                        {...cellProps}
                        borderTopWidth="1px"
                        borderLeftWidth="1px"
                        borderColor="border"
                        color="fg.muted"
                        whiteSpace="nowrap"
                      >
                        {row.radius}
                      </Table.Cell>
                      <Table.Cell
                        {...cellProps}
                        borderTopWidth="1px"
                        borderLeftWidth="1px"
                        borderColor="border"
                        whiteSpace="nowrap"
                      >
                        <HStack gap={1} wrap="nowrap">
                          {row.frame.map((val, i) => (
                            <Badge
                              key={i}
                              colorPalette="gray"
                              variant="plain"
                              size="sm"
                              whiteSpace="nowrap"
                            >
                              {formatQuantity(val, CAPACITY_UNITS[i]!)}
                            </Badge>
                          ))}
                        </HStack>
                      </Table.Cell>
                      <Table.Cell
                        {...cellProps}
                        borderTopWidth="1px"
                        borderLeftWidth="1px"
                        borderColor="border"
                        whiteSpace="nowrap"
                      >
                        <HStack gap={1} wrap="nowrap">
                          {row.ordered.map((val, i) => {
                            const overCapacity =
                              parseNumber(val) > parseNumber(row.frame[i] ?? '0')
                            return (
                              <Badge
                                key={i}
                                colorPalette={overCapacity ? 'red' : 'gray'}
                                variant="plain"
                                size="sm"
                                whiteSpace="nowrap"
                              >
                                {formatQuantity(val, CAPACITY_UNITS[i]!)}{' '}
                                {capacityUsagePct(val, row.frame[i] ?? '0')}
                              </Badge>
                            )
                          })}
                        </HStack>
                      </Table.Cell>
                      <Table.Cell
                        {...cellProps}
                        borderTopWidth="1px"
                        borderLeftWidth="1px"
                        borderColor="border"
                        whiteSpace="nowrap"
                      >
                        <ZeitzielBadge value={row.target} />
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          </Box>
          {/* Fade hint for horizontal scroll */}
          {hasOverflow && (
            <Box
              position="absolute"
              top={0}
              right={0}
              bottom={0}
              w="48px"
              pointerEvents="none"
              bgGradient="linear(to-r, transparent, bg 80%)"
              zIndex={3}
            />
          )}
        </Box>
      </Box>
    </Stack>
  )
}

export function SibCapacityTableHeader() {
  return (
    <HStack gap={3} align="center">
      <Avatar.Root size="sm" variant="subtle" colorPalette="blue">
        <Avatar.Fallback />
      </Avatar.Root>
      <Text fontSize="sm" color="fg">
        SIB
      </Text>
    </HStack>
  )
}
